import * as React from 'react';
import { Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import GenericHoc from 'components/common/generic-hoc';
import { HocOptions } from 'components/common/generic-hoc.types';
import { StylesProps } from 'theme/jss-types';
import { PickupAddress } from 'components/pickup/create-pickup/pickup.types';
import {
    dockMappingFromApiArray, DockMappingValue,
} from 'components/address/DockVehicleMapping';
import { addressFieldStyles } from './address-field.styles';

interface AddressDetailsModalProps extends StylesProps<typeof addressFieldStyles> {
    item: PickupAddress;
    visible: boolean;
    onClose: () => void;
}

interface FieldDef {
    label: string;
    value: React.ReactNode;
}

interface SectionDef {
    title: string;
    fields: FieldDef[];
}

const hasValue = (value: React.ReactNode): boolean => {
    if (value === null || value === undefined || value === '') return false;
    if (Array.isArray(value) && value.length === 0) return false;
    if (typeof value === 'object' && !React.isValidElement(value)
        && Object.keys(value as Record<string, unknown>).length === 0) {
        return false;
    }
    return true;
};

const formatBool = (value?: boolean): React.ReactNode => (value ? 'Yes' : null);

const formatDuration = (value?: number | string | null): React.ReactNode => {
    if (value === null || value === undefined || value === '') return null;
    return typeof value === 'number' ? `${value} min` : value;
};

// timeSlotList is stored as an array of [start, end] tuples (string[][]).
// Renders every slot, one line each — no truncation.
const formatTimeSlots = (slots?: any[] | null): React.ReactNode => {
    if (!Array.isArray(slots) || slots.length === 0) return null;
    const lines = slots
        .map((slot) => {
            if (Array.isArray(slot)) return slot.filter((v) => v || v === 0).join(' - ');
            if (slot && typeof slot === 'object') {
                const start = slot.start ?? slot.startTime ?? '';
                const end = slot.end ?? slot.endTime ?? '';
                return [start, end].filter(Boolean).join(' - ');
            }
            return slot ? String(slot) : '';
        })
        .filter(Boolean);
    if (!lines.length) return null;
    return (
        <>
            {lines.map((line, idx) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={idx}>{line}</div>
            ))}
        </>
    );
};

// dockVehicleMapping may arrive as the API array form (DockEntry[]) or the
// record form (DockMappingValue). Normalises to the record form and renders
// every dock with all its fields — vehicle makes, operating hours, recycle time.
const formatMapping = (mapping?: any): React.ReactNode => {
    if (!mapping) return null;
    const record: DockMappingValue = Array.isArray(mapping)
        ? dockMappingFromApiArray(mapping)
        : mapping;
    const entries = Object.entries(record || {});
    if (!entries.length) return null;
    return (
        <>
            {entries.map(([dockNumber, entry]) => {
                const makes = Array.isArray(entry?.vehicleMakes) && entry.vehicleMakes.length
                    ? entry.vehicleMakes.join(', ')
                    : null;
                const hours = entry?.startTime && entry?.endTime
                    ? `${entry.startTime} - ${entry.endTime}`
                    : null;
                const recycle = entry?.recycleTimeMinutes != null
                    ? `recycle ${entry.recycleTimeMinutes} min`
                    : null;
                const meta = [makes, hours, recycle].filter(Boolean).join(' · ');
                return (
                    <div key={dockNumber}>
                        <b>{`Dock ${dockNumber}`}</b>
                        {meta ? `: ${meta}` : ''}
                    </div>
                );
            })}
        </>
    );
};

const AddressDetailsModalComponent = (props: AddressDetailsModalProps) => {
    const { t } = useTranslation();
    const {
        classes,
        item,
        visible,
        onClose,
    } = props;

    const sections = React.useMemo<SectionDef[]>(() => {
        const rawSections: SectionDef[] = [
            {
                title: 'section_identity',
                fields: [
                    { label: 'name', value: item.name },
                    { label: 'address_code', value: item.addressCode },
                    { label: 'company_name', value: item.companyName },
                ],
            },
            {
                title: 'section_address',
                fields: [
                    { label: 'address_line_1', value: item.addressLine1 },
                    { label: 'address_line_2', value: item.addressLine2 },
                    { label: 'address_line_3', value: item.addressLine3 },
                    { label: 'address_line_4', value: item.addressLine4 },
                    { label: 'address_line_5', value: item.addressLine5 },
                    { label: 'district', value: item.district },
                    { label: 'cityName', value: item.cityName },
                    { label: 'pincode', value: item.pincode },
                    { label: 'stateName', value: item.stateName },
                    { label: 'countryName', value: item.countryName },
                    { label: 'international', value: formatBool(item.isInternational) },
                ],
            },
            {
                title: 'section_contact',
                fields: [
                    { label: 'phone', value: item.phone },
                    { label: 'alternate_phone', value: item.alternatePhone },
                    { label: 'alternate_phone_2', value: item.alternatePhone2 },
                    { label: 'email', value: item.email },
                    { label: 'notes', value: item.notes },
                ],
            },
            {
                title: 'section_location',
                fields: [
                    { label: 'location_id', value: item.locationId },
                    { label: 'location_code', value: item.locationCode },
                    { label: 'client_id', value: item.clientId },
                ],
            },
            {
                title: 'section_operations',
                fields: [
                    { label: 'service_time', value: formatDuration(item.serviceTime) },
                    { label: 'grace_time', value: formatDuration(item.graceTime) },
                    { label: 'dock_capacity', value: item.dockCapacity },
                    { label: 'time_slots', value: formatTimeSlots(item.timeSlotList) },
                    { label: 'dock_vehicle_mapping', value: formatMapping(item.dockVehicleMapping) },
                ],
            },
        ];

        return rawSections
            .map((section) => ({
                ...section,
                fields: section.fields.filter((field) => hasValue(field.value)),
            }))
            .filter((section) => section.fields.length > 0);
    }, [item]);

    return (
        <Modal
            title={t('address_details_title')}
            open={visible}
            onCancel={onClose}
            footer={null}
            width={560}
            destroyOnClose
        >
            {sections.map((section) => (
                <div key={section.title} className={classes.detailsSection}>
                    <div className={classes.detailsSectionTitle}>{t(section.title)}</div>
                    {section.fields.map((field) => (
                        <div key={field.label} className={classes.detailsRow}>
                            <span className={classes.detailsLabel}>{t(field.label)}</span>
                            <span className={classes.detailsValue}>{field.value}</span>
                        </div>
                    ))}
                </div>
            ))}
        </Modal>
    );
};

const hocConfig: HocOptions = {
    connectJss: {
        useJss: true,
        styleSheet: addressFieldStyles,
    },
};

export default GenericHoc(hocConfig)(AddressDetailsModalComponent);
